/**
 * Feature hook: Preview in Teams – one method for IFS (upload then preview) or OneDrive document (preview by id).
 * Pass IFS item (has class, docNo) or OneDrive document (has id/documentId/key). Same loading/error state and API (getOneDrivePreviewUrl).
 */
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../hooks";
import {
  previewInTeams as previewInTeamsThunk,
  clearPreviewUrl as clearPreviewUrlAction,
  clearPreviewInTeamsError as clearPreviewInTeamsErrorAction,
  setPreviewDialogOpen as setPreviewDialogOpenAction,
  setPreviewError as setPreviewErrorAction,
} from "../slices/documents/previewInTeamsSlice";
import { useLogin } from "./useLogin";
import { useTeamsRequestContext } from "./useTeamsRequestContext";
import { APP_ROUTES } from "../../routes/routeConfig";
import { logger } from "../../utils/logger";
import {
  PREVIEW_LOADING_IFS,
  PREVIEW_LOADING_ONEDRIVE,
} from "../constants/messageTypes";

const ONEDRIVE_ROOT_MAIN = "MainMenu";
const ONEDRIVE_ROOT_CHANNEL = "ChannelDoc";
const PREVIEW_DIALOG_AUTO_CLOSE_MS = 3000;

function isIFSItem(x) {
  return x != null && x.class != null && x.docNo != null;
}

export function usePreviewInTeams() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { teamsContext } = useLogin();
  const {
    tenantId: requestTenantId,
    groupId: requestGroupId,
    channelId: requestChannelId,
  } = useTeamsRequestContext();
  const previewState = useAppSelector((state) => state.PreviewInTeams);

  const previewInTeams = useCallback(
    (itemOrDocument, returnTo, returnState) => {
      if (!itemOrDocument) return;
      const tenantId =
        teamsContext?.user?.tenant?.id ?? requestTenantId ?? "";
      if (!tenantId) {
        dispatch(setPreviewDialogOpenAction(true));
        dispatch(setPreviewErrorAction({ message: "Missing sign-in or context. Please try again." }));
        setTimeout(() => dispatch(setPreviewDialogOpenAction(false)), PREVIEW_DIALOG_AUTO_CLOSE_MS);
        return;
      }

      // IMPORTANT: In general-app mode, stored requestGroupId/requestChannelId can be stale.
      // For OneDrive MainMenu docs we must use teamId="" and channelId="" so backend queries user drive.
      const isGeneralAppContext = !teamsContext?.channel?.id;
      const teamId =
        teamsContext?.channel != null
          ? teamsContext.channel.membershipType === "Private"
            ? teamsContext.channel.ownerGroupId
            : teamsContext.team?.groupId
          : isGeneralAppContext
            ? ""
            : requestGroupId ?? "";
      const channelId =
        teamsContext?.channel != null
          ? teamsContext.channel.id
          : isGeneralAppContext
            ? ""
            : (requestChannelId ?? "");

      if (isIFSItem(itemOrDocument)) {
        dispatch(
          previewInTeamsThunk({
            item: itemOrDocument,
            teamId,
            channelId,
            tenantId,
            navigate,
            returnTo: returnTo ?? APP_ROUTES.allDocuments,
            returnState: returnState ?? {},
            loadingMessage: PREVIEW_LOADING_IFS,
          })
        );
        return;
      }

      const documentId =
        itemOrDocument?.id ?? itemOrDocument?.documentId ?? itemOrDocument?.key ?? "";
      if (!documentId) {
        logger.warn("[usePreviewInTeams] OneDrive preview missing documentId", { itemOrDocument }, "PREVIEW");
        dispatch(setPreviewDialogOpenAction(true));
        dispatch(setPreviewErrorAction({ message: "Document ID is missing. Cannot open preview." }));
        setTimeout(() => dispatch(setPreviewDialogOpenAction(false)), PREVIEW_DIALOG_AUTO_CLOSE_MS);
        return;
      }
      const fileRoot =
        (teamId === "" || teamId == null) && (channelId === "" || channelId == null)
          ? ONEDRIVE_ROOT_MAIN
          : ONEDRIVE_ROOT_CHANNEL;
      logger.info("[usePreviewInTeams] OneDrive preview", { documentId, fileRoot, returnTo }, "PREVIEW");
      dispatch(
        previewInTeamsThunk({
          documentId,
          fileRoot,
          teamId,
          channelId,
          tenantId,
          navigate,
          returnTo: returnTo ?? APP_ROUTES.workingDocuments,
          returnState: returnState ?? {},
          loadingMessage: PREVIEW_LOADING_ONEDRIVE,
        })
      );
    },
    [
      dispatch,
      navigate,
      teamsContext,
      requestTenantId,
      requestGroupId,
      requestChannelId,
    ]
  );

  const clearPreviewUrl = useCallback(() => {
    dispatch(clearPreviewUrlAction());
  }, [dispatch]);

  const clearPreviewInTeamsError = useCallback(() => {
    dispatch(clearPreviewInTeamsErrorAction());
  }, [dispatch]);

  const closePreviewDialog = useCallback(() => {
    dispatch(setPreviewDialogOpenAction(false));
  }, [dispatch]);

  return {
    state: {
      loading: previewState?.loading ?? false,
      error: previewState?.error ?? null,
      status: previewState?.status ?? "idle",
      loadingMessage: previewState?.loadingMessage ?? "",
      previewUrl: previewState?.previewUrl ?? "",
      isOpen: previewState?.isOpen ?? false,
      isError: previewState?.status === "error" || !!previewState?.error,
      message: previewState?.message ?? "",
    },
    previewInTeams,
    clearPreviewUrl,
    clearPreviewInTeamsError,
    closePreviewDialog,
  };
}
